import { Dispatch, SetStateAction, useEffect, useState } from "react";

import Modal from "../../layouts/Modal";
import SelectableTable from "../shared/Tables/SelectableTable";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useCategoriesPartial from "../../data/useCategoriesPartial";
import { IPartTagTemplate } from "../../models/PartTagTemplate";
import { categoryOwnerMap } from "../../utils/partTagFields";

interface PartTagCategoriesModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  tag: IPartTagTemplate;
  tags: IPartTagTemplate[];
  onApply: (categoryIds: string[]) => void;
}

export default function PartTagCategoriesModal({ open, setOpen, tag, tags, onApply }: PartTagCategoriesModalProps) {
  const { categories } = useCategoriesPartial();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) setSelectedIds([...(tag.categoryIds ?? [])]);
  }, [open, tag]);

  const owners = categoryOwnerMap(tags);

  // Flag categories that another tag holds, since ticking one here moves it.
  const options = (categories.data?.data ?? []).map((c) => {
    const owner = owners[c.id];
    const heldElsewhere = owner && owner.tagId !== tag.id;
    return { id: c.id, name: heldElsewhere ? `${c.name} (currently: ${owner.name})` : c.name };
  });

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-3xl">
      <div className="sm:flex sm:items-start">
        <div className="mt-3 text-left sm:mt-0 sm:ml-2 sm:text-left w-full">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Select Categories for {tag.name}</h3>
          <p className="mt-1 text-sm text-gray-600">
            A category can only belong to one tag. Selecting a category here removes it from its current tag.
            Categories not assigned to any tag use the Default tag.
          </p>
          <SelectableTable
            options={options}
            isLoading={categories.isLoading}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            placeholder="Search categories"
          />
        </div>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <ButtonInput label={"Apply Selected"} isSubmit={false} onClick={() => { onApply(selectedIds); setOpen(false); }} classes="sm:ml-3 sm:w-auto w-full px-4" />
        <ButtonInput label={"Cancel"} isSubmit={false} onClick={() => setOpen(false)} classes="mt-3 sm:mt-0 sm:w-auto w-full bg-white !text-gray-700 ring-1 ring-inset ring-gray-300 hover:!bg-gray-50 px-4" />
      </div>
    </Modal>
  );
}
